import React, { cloneElement, FC, useEffect, useState } from 'react';
import {
  List,
  useListContext,
  TopToolbar,
  sanitizeListRestProps,
  useRefresh,
  useNotify,
  Pagination,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import { MasterSheetFilters } from '../filters/master-sheet-filters';
import { makeStyles } from '@material-ui/core';
import { exporter } from 'providers/export';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { AppRole } from 'providers/roles';
import { axiosInstance } from 'services/http';
import CustomExportButton from 'components/CustomExportButton';
import { MapProvider } from 'components/Map';
import { trackEvent } from 'utils/mixpanel';
import {
  WorkflowConfigProvider,
  useWorkflowConfig,
  DRYWALL_AND_PAINT_CONFIG,
} from '../config';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { EditableDateColumnDef } from '../config/types';
import { renderColumns, FieldsMap } from '../components/list/renderColumns';
import { PostBulkActionButtons } from '../components/list/PostBulkActionButtons';

const useStyles = makeStyles({
  content: {
    '@media(max-width: 1550px)': {
      overflow: 'auto',
    },
  },
  contentClosed: {
    '@media(max-width: 1550px)': {
      overflow: 'auto',
    },
  },
  root: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 260px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  rootClosed: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 125px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
});

const ListActions: FC<any> = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    total,
  } = useListContext();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

const MasterSheetListInner: FC<any> = props => {
  const config = useWorkflowConfig();
  const { hasRole } = PermissionsProvider.useContainer();
  const classes = useStyles(props);
  const notify = useNotify();
  const refresh = useRefresh();
  const [fields, setFields] = useState<FieldsMap>({});
  const { opened } = UIProvider.useContainer();
  const [loading, setLoading] = useState(false);

  const isSupervisor = hasRole(AppRole.Supervisor);
  const isAdmin = hasRole(AppRole.SuperAdmin) || hasRole(AppRole.RegionalAdmin);

  const isWarranty =
    window.location.href.indexOf(config.edit.warrantyUrlSegment) !== -1;

  const filterDefault = { 'activeJob||$eq': true };

  const onFieldChange =
    (field: string) =>
    (id: string) =>
    (event: { target: { value: MaterialUiPickersDate | boolean } }) =>
      setFields(oldFields => ({
        ...oldFields,
        [id]: { ...oldFields[id], [field]: event.target.value },
      }));

  // Extract editable date columns from config for submit logic
  const editableDateCols = config.list.columns.filter(
    (c): c is EditableDateColumnDef => c.type === 'editable-date',
  );

  const submitDates = async (record: { id: unknown; [k: string]: unknown }) => {
    const { id, jobId } = record;
    const stringfiedId = String(id);
    const row = fields[stringfiedId];
    if (!row) return;

    setLoading(true);
    try {
      const data: Record<string, string | null> = {};
      for (const col of editableDateCols) {
        if (Object.keys(row).indexOf(col.customDateField) !== -1) {
          const val = row[col.customDateField];
          data[col.customDateField] =
            val && typeof val === 'object' && 'toISO' in val
              ? (val as { toISO(): string }).toISO()
              : null;
        }
      }

      await axiosInstance.patch(`/job/${jobId}/custom-phase-date`, data);
      notify('Changes were made successfully!');
      refresh();
    } catch (error: unknown) {
      const newFields = { ...fields };
      // Reset changed fields to original values on error
      for (const col of editableDateCols) {
        if (
          newFields[stringfiedId] &&
          col.customDateField in newFields[stringfiedId]
        ) {
          newFields[stringfiedId][col.customDateField] = record[
            col.dateSource
          ] as FieldsMap[string][string];
        }
      }
      setFields(newFields);
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      notify(`${err.response?.data?.message || err.message}`, 'warning');
    } finally {
      const newFields = { ...fields };
      newFields[stringfiedId].submitted = true;
      setFields(newFields);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (config.tracking.mixpanelEnabled) {
      trackEvent('PageView', {
        resource:
          config.phaseStructureType === 'Drywall'
            ? 'master-sheet'
            : 'master-sheet-v2',
        page: window.location.pathname,
        role: localStorage.getItem('userRoles'),
        env: process.env.REACT_APP_MIXPANEL_ENV,
      });
    }
  }, []);

  const sort = isWarranty
    ? { field: 'bump1PhaseDateStart', order: 'ASC' as const }
    : config.list.defaultSort;

  const { perPage, rowsPerPageOptions } = config.list.pagination;

  const bulkActionButtons =
    config.list.bulkActionsEnabled && !isWarranty ? (
      <PostBulkActionButtons />
    ) : (
      false
    );

  return (
    <List
      className="list"
      {...props}
      exporter={exporter(
        config.phaseStructureType === 'Drywall'
          ? 'master-sheet'
          : 'master-sheet-v2',
      )}
      filters={<MasterSheetFilters isWarranty={isWarranty} />}
      sort={sort}
      bulkActionButtons={bulkActionButtons}
      actions={<ListActions maxResults={20000} />}
      classes={{
        content: opened ? classes.content : classes.contentClosed,
        root: opened ? classes.root : classes.rootClosed,
      }}
      filterDefaultValues={isWarranty ? {} : filterDefault}
      pagination={
        <Pagination
          rowsPerPageOptions={isWarranty ? [5, 10, 25] : rowsPerPageOptions}
        />
      }
      perPage={isWarranty ? 10 : perPage}
    >
      <Datagrid
        rowClick={(id, basePath, _record) => `${basePath}/${id}`}
        rowStyle={config.list.rowStyleFn}
        isHeaderSelectable={config.list.bulkActionsEnabled && !isWarranty}
        isRowSelectable={
          config.list.bulkActionsEnabled ? () => !isWarranty : undefined
        }
      >
        {renderColumns(config.list.columns, {
          fields,
          onFieldChange,
          loading,
          submitDates,
          isWarranty,
          isSupervisor,
          isAdmin,
        })}
      </Datagrid>
    </List>
  );
};

export const MasterSheetList: FC<any> = ({ config, ...props }) => (
  <WorkflowConfigProvider config={config ?? DRYWALL_AND_PAINT_CONFIG}>
    <MapProvider>
      <MasterSheetListInner {...props} />
    </MapProvider>
  </WorkflowConfigProvider>
);
